// Interactive world map — the Overview geography filter surface.
// Fill intensity uses ONLY the published structuralActivity.share
// (regional structural activity share). Geography carries NO analytical
// score. Actor-group aggregations are intentionally not rendered.
// Visual encoding (sqrt ramp) is presentation-only — values untouched.
import React, { useEffect, useMemo, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";

const REGION_TO_CONTINENT = {
    north_america: "North America",
    south_america: "South America",
    europe: "Europe",
    africa: "Africa",
    asia: "Asia",
    oceania: "Oceania",
};

// Published dynamics enum → user-facing label + tone. Never translated into strength.
export const DYNAMICS_LABEL = {
    GAINING_SHARE: ["Gaining share", "text-[hsl(var(--positive))]"],
    STABLE_SHARE: ["Stable share", "text-[hsl(var(--accent-ink))]"],
    LOSING_SHARE: ["Losing share", "text-[hsl(var(--warning))]"],
    NOT_CLASSIFIABLE: ["Insufficient recent basis", "text-[hsl(var(--mute))]"],
};

const pct1 = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

// Sequential activity ramp — sqrt lifts low/medium shares for perceptual
// differentiation. Pure display encoding of the published share.
const rampOpacity = (share, maxShare) =>
    share == null ? 0 : 0.14 + 0.86 * Math.sqrt(share / maxShare);

export const WorldPresenceMap = ({ regions, selectedGeoKey, onSelect }) => {
    const [features, setFeatures] = useState(null);
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch("/geo/continents.json")
            .then((r) => r.json())
            .then((d) => { if (!cancelled) setFeatures(d.features); })
            .catch(() => { if (!cancelled) setFeatures([]); });
        return () => { cancelled = true; };
    }, []);

    const byContinent = useMemo(() => {
        const map = {};
        for (const r of regions || []) {
            const cont = REGION_TO_CONTINENT[r.geoKey];
            if (cont) map[cont] = r;
        }
        return map;
    }, [regions]);

    const { pathFor } = useMemo(() => {
        const projection = geoNaturalEarth1().scale(158).translate([480, 250]);
        return { pathFor: geoPath(projection) };
    }, []);

    const maxShare = Math.max(...(regions || []).map((r) => r.structuralActivity?.share || 0), 0.01);
    const selectedCont = Object.keys(REGION_TO_CONTINENT).includes(selectedGeoKey) ? REGION_TO_CONTINENT[selectedGeoKey] : null;

    return (
        <div className="card-flat p-3 md:p-4" data-testid="wp12-home-worldmap">
            <div className="flex flex-col lg:flex-row gap-3">
                {/* Tight viewBox: world without Antarctica */}
                <div className="w-full lg:flex-1 min-w-0">
                    <svg viewBox="70 8 820 400" className="w-full select-none" role="img" aria-label="Geographic structural activity map">
                    {features === null && <text x="480" y="200" textAnchor="middle" className="text-xs" fill="hsl(var(--mute))">Loading map…</text>}
                    {(features || []).map((f, i) => {
                        const cont = f.properties.continent;
                        const region = byContinent[cont];
                        const share = region?.structuralActivity?.share ?? null;
                        const intensity = rampOpacity(share, maxShare);
                        const isSelected = selectedCont === cont;
                        const isHovered = hovered === cont && region;
                        const fill = !region
                            ? "hsl(var(--line) / 0.4)"
                            : isSelected
                                ? `hsl(275 42% 42% / ${Math.min(1, intensity + 0.12).toFixed(2)})`
                                : `hsl(222 48% 42% / ${intensity.toFixed(2)})`;
                        return (
                            <path
                                key={i}
                                d={pathFor(f)}
                                fill={fill}
                                stroke={isSelected ? "hsl(var(--accent))" : isHovered ? "hsl(var(--accent) / 0.6)" : "hsl(var(--surface) / 0.6)"}
                                strokeWidth={isSelected ? 1.1 : isHovered ? 0.8 : 0.3}
                                style={{ cursor: region ? "pointer" : "default", transition: "fill 200ms" }}
                                onMouseEnter={() => region && setHovered(cont)}
                                onMouseLeave={() => setHovered(null)}
                                onClick={() => region && onSelect?.(selectedGeoKey === region.geoKey ? null : region.geoKey)}
                            >
                                <title>{region ? `${region.label} — ${pct1(region.structuralActivity?.share)} of observed structural activity · click to ${isSelected ? "clear" : "set"} filter` : cont}</title>
                            </path>
                        );
                    })}
                    </svg>
                    <div className="flex items-center gap-2 px-1" data-testid="wp12-home-map-scale">
                        <span className="text-[9px] text-[hsl(var(--mute))]">Observed structural activity share</span>
                        <span className="text-[9px] text-[hsl(var(--mute))]">Low</span>
                        <span className="h-1.5 w-24 rounded-full" style={{ background: "linear-gradient(to right, hsl(222 48% 42% / 0.14), hsl(222 48% 42%))" }} />
                        <span className="text-[9px] text-[hsl(var(--mute))]">High</span>
                    </div>
                </div>

                {/* Published regional shares + dynamics — filter controls */}
                <div className="lg:w-60 flex-shrink-0 flex flex-col gap-1.5" data-testid="wp12-home-worldmap-panel">
                    <button
                        type="button"
                        data-testid="wp12-home-geo-all"
                        onClick={() => onSelect?.(null)}
                        className={`w-full text-left px-3 py-1.5 rounded border text-[11px] transition ${
                            !selectedGeoKey
                                ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]/50 font-medium"
                                : "border-[hsl(var(--line))] hover:bg-[hsl(var(--surface-sunken))]"
                        }`}
                    >
                        All regions
                    </button>
                    {(regions || []).map((r) => {
                        const isSelected = selectedGeoKey === r.geoKey;
                        const dyn = DYNAMICS_LABEL[r.dynamics?.label];
                        return (
                            <button
                                key={r.geoKey}
                                type="button"
                                data-testid={`wp12-home-geo-${r.geoKey}`}
                                onMouseEnter={() => setHovered(REGION_TO_CONTINENT[r.geoKey])}
                                onMouseLeave={() => setHovered(null)}
                                onClick={() => onSelect?.(isSelected ? null : r.geoKey)}
                                className={`w-full text-left px-3 py-1.5 rounded border transition ${
                                    isSelected
                                        ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]/50"
                                        : "border-[hsl(var(--line))] hover:bg-[hsl(var(--surface-sunken))]"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-medium">{r.label}</span>
                                    <span className="num text-[13px] font-semibold">{pct1(r.structuralActivity?.share)}</span>
                                </div>
                                <div className={`text-[10px] mt-0.5 ${dyn ? dyn[1] : "text-[hsl(var(--mute))]"}`} data-testid={`wp12-home-geo-dynamics-${r.geoKey}`}>
                                    {dyn ? dyn[0] : "—"}
                                    <span className="text-[hsl(var(--mute))]"> · {r.associatedPatternCount ?? "—"} patterns</span>
                                </div>
                            </button>
                        );
                    })}
                    <p className="text-[9px] text-[hsl(var(--mute))] leading-snug mt-auto pt-1" data-testid="wp12-home-geo-coverage-note">
                        Geography coverage spans 6 observed weeks overall; region-level observations vary.
                    </p>
                </div>
            </div>
        </div>
    );
};
